import { makeAutoObservable } from 'mobx';
import JobTimelineEntryViewModel from './JobTimelineEntryViewModel';
import _ from 'lodash';
import {auth, endpoints, types} from 'main/tools';
import {DomainStore} from 'shared/store';
import {OnLeaveRecord, TerminationRecord, EmploymentRecord, ReactivationRecord} from 'main/stores/employees';
import {tabResourceId, groupEmployeeCustomFields} from 'shared/tools';
import {EmployeeCustomField} from 'main/stores/employee_custom_fields';

class EmployeeRoleState {
  store = new DomainStore();
  employeeId;
  history;

  customFieldGroups;
  entries = [];
  deleteRecord = null;
  deleteModalOpen = false;
  onLeaveModalOpen = false;
  scheduledForTermination;
  editConfirmationModalOpen = false;
  editRecord;
  employmentRecordCustomFields = [];
  scope = {};

  constructor() {
    makeAutoObservable(this, {
      store: false,
      history: false
    });
  }

  receiveProps({parentState, history}) {
    this.parentState = parentState;
    this.history = history;
  }

  async load() {
    this.employeeId = this.parentState.match.params.id;

    await this.store._compose([
      endpoints.CUSTOM_FIELD_GROUPS.with('job')
    ]);

    this.customFieldGroups = this.store._getAll(types.CUSTOM_FIELD_GROUP);

    await this.refreshRecords();
  }

  async loadEmploymentRecords(employeeId) {
    const store = new DomainStore();

    await store._compose([
      endpoints.EMPLOYEE.EMPLOYMENT_HISTORY.with(employeeId),
      endpoints.EMPLOYEE.EMPLOYMENT_HISTORY.NAVIGATION.with(employeeId),
      endpoints.LOCATIONS.ALL,
      endpoints.DEPARTMENTS,
      endpoints.LEAVE_TYPES,
      endpoints.EMPLOYEE_CUSTOM_FIELDS.LIST_EMPLOYMENT_RECORD_CUSTOM_FIELDS_FOR_EMPLOYEE.with(employeeId)
    ]);

    const employmentRecords = store._getAll(types.EMPLOYMENT_RECORD).map(r => new EmploymentRecord(r));
    const terminationRecords = store._getAll(types.TERMINATION_RECORD).map(r => new TerminationRecord(r));
    const onLeaveRecords = store._getAll(types.ON_LEAVE_RECORD).map(r => new OnLeaveRecord(r));
    const reactivationRecords = store._getAll(types.REACTIVATION_RECORD).map(r => new ReactivationRecord(r));
    this.scope = store._getSingle(types.NAVIGATION_SCOPE, {id: tabResourceId('employee', this.employeeId)}).tabs;

    this.employmentRecordCustomFields = store._getAll(types.EMPLOYEE_CUSTOM_FIELD, EmployeeCustomField);

    return [...employmentRecords, ...terminationRecords, ...onLeaveRecords, ...reactivationRecords];
  }

  async refreshRecords() {
    this.entries = (await this.loadEmploymentRecords(this.employeeId)).map(record => new JobTimelineEntryViewModel(record));
    this.updateFirstAndLast();
    this.currentRecordViewModel.expanded = true;
  }

  updateFirstAndLast() {
    const entries = this.sortedEntries;
    const firstRecord = _.head(entries);
    const lastRecord = _.last(entries);
    for (const record of entries) {
      record.isFirst = firstRecord === record;
      record.isLast = lastRecord === record;
    }
  }

  createNewRecord() {
    this.history.push(`/${this.employeeId}/job/records/create`);
  }

  showDeleteModal(record) {
    this.deleteRecord = record;
    this.deleteModalOpen = true;
  }

  openEditConfirmationModal(record) {
    this.editRecord = record;
    this.editConfirmationModalOpen = true;
  }

  closeEditConfirmationModal() {
    this.editRecord = null;
    this.editConfirmationModalOpen = false;
  }

  editEntry() {
    this.history.push(`/${this.employeeId}/job/records/${this.editRecord.id}`);
  }

  cancelDeleteModal() {
    this.deleteRecord = null;
    this.deleteModalOpen = false;
  }

  async destroyEmploymentRecord() {
    await this.store.destroy(this.deleteRecord);
    await this.refreshRecords();
    this.deleteModalOpen = false;
  }

  terminateEmployee() {
    this.history.push(`/${this.employeeId}/job/records/terminate`);
  }

  reactivateEmployee() {
    this.history.push(`/${this.employeeId}/job/records/reactivate`);
  }

  handleOnLeave() {
    if (this.parentState.employee.enrolledInBenefits) {
      this.openOnLeaveModal();
    } else {
      this.placeOnLeave();
    }
  }

  placeOnLeave() {
    this.history.push(`/${this.employeeId}/job/records/leave`);
  }

  openOnLeaveModal() {
    this.onLeaveModalOpen = true;
  }

  closeOnLeaveModal() {
    this.onLeaveModalOpen = false;
  }

  customFieldGroupsForRecord(record) {
    return groupEmployeeCustomFields(this.customFieldsForRecord(record), this.customFieldGroups);
  }

  customFieldsForRecord(record) {
    return _.filter(this.employmentRecordCustomFields, {employmentRecordId: record.id});
  }

  get currentRecordViewModel() {
    return _.find(this.entries, r => r.record.current);
  }

  get sortedEntries() {
    return _.orderBy(this.entries, ['record.effectiveDate'], ['desc']);
  }

  get canTerminate() {
    return this.scope.createTerminationRecord;
  }

  get canReactivate() {
    return this.scope.createReactivationRecord;
  }

  get validActions() {
    const actionKeys = _.keys(this.scope);

    return _.filter(actionKeys, (key) => this.scope[key]);
  }

  get recordType() {
    return _.get(this.deleteRecord, '_type');
  }

  get payrollEnabled() {
    return this.parentState.employee.payrollEnabled && auth.company.payrollEnabled;
  }
}

export default EmployeeRoleState;
